import type { ReactNode } from 'react';
import { EmptyState } from '@/components/ui';
import { InlineField, type InlineSaveResult } from '@/components/ui/InlineField';
import { Money } from '@/components/ui/Money';
import { api, ApiError } from '@/lib/api';
import { formatDecimalString } from '@/lib/format';
import type { SalesTargetLine, SalesTargetsView } from '@/lib/types';
import { groupSalesTargetLines } from './sales-targets-export';

/**
 * Sales targets below `md` (owner 2026-10-28): the project lines as stacked
 * cards, grouped by product, carrying the same fields as `SalesTargetsTable`.
 * The root is `md:hidden`, so the panel renders it next to the table.
 *
 * Editable fields write through `api.upsertSalesTargetLine` (PATCH semantics:
 * only the changed key is sent, `''` → `null` clears it) and call `onChanged`
 * so the panel refetches the computed figures.
 */

type EditableField =
  | 'institutionPlanned'
  | 'institutionTarget'
  | 'partnerPlanned'
  | 'partnerTarget'
  | 'travelPercent'
  | 'months'
  | 'unitPriceOverrideUsd'
  | 'notes';

const DECIMAL = /^\d+(\.\d{1,2})?$/;

function validateAmount(next: string): string | null {
  if (next === '') return null;
  return DECIMAL.test(next) ? null : 'Enter a number of zero or more, with at most two decimals.';
}

function validatePercent(next: string): string | null {
  const base = validateAmount(next);
  if (base !== null || next === '') return base;
  // A percentage, not money: comparing the whole part as an integer is exact.
  const [whole = '0', fraction = ''] = next.split('.');
  const wholeValue = Number.parseInt(whole, 10);
  const over = wholeValue > 100 || (wholeValue === 100 && /[1-9]/.test(fraction));
  return over ? 'Enter a percentage from 0 to 100.' : null;
}

function text(value: string | number | null | undefined): string {
  return value === null || value === undefined ? '' : String(value);
}

function count(value: string | number | null | undefined): string {
  return formatDecimalString(value === null || value === undefined ? null : String(value));
}

function errorMessage(error: unknown, field: EditableField): string {
  if (error instanceof ApiError) {
    return error.fieldErrors[field] ?? error.formMessage ?? error.message;
  }
  return 'The change could not be saved.';
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-fg-muted">{label}</dt>
      <dd className="m-0 min-w-0 text-right text-fg-secondary">{children}</dd>
    </>
  );
}

function LineCard({
  line,
  view,
  poolName,
  canEdit,
  onChanged,
}: {
  line: SalesTargetLine;
  view: SalesTargetsView;
  poolName: string | null;
  canEdit: boolean;
  onChanged: () => void;
}) {
  async function save(field: EditableField, next: string): Promise<InlineSaveResult> {
    try {
      await api.upsertSalesTargetLine({
        cycleId: view.cycle.id,
        projectId: line.projectId,
        [field]: next === '' ? null : next,
      });
      onChanged();
      return { status: 'saved' };
    } catch (error) {
      return { status: 'error', message: errorMessage(error, field) };
    }
  }

  function editable(
    field: EditableField,
    label: string,
    value: string,
    display: ReactNode,
    validate: (next: string) => string | null,
  ) {
    return (
      <InlineField
        label={`${label} · ${line.projectName}`}
        value={value}
        display={display}
        disabled={!canEdit}
        inputMode="decimal"
        validate={validate}
        onSave={(next) => save(field, next)}
      />
    );
  }

  const meta = [line.regions.join(', '), line.intake].filter((part) => part !== null && part !== '');

  return (
    <li className="flex flex-col gap-1.5 py-3">
      <div className="min-w-0">
        <p className="m-0 text-sm font-medium text-fg [overflow-wrap:anywhere]">{line.projectName}</p>
        {meta.length > 0 && <p className="m-0 text-xs text-fg-muted">{meta.join(' · ')}</p>}
      </div>
      <dl className="m-0 grid grid-cols-[minmax(0,1fr)_minmax(0,auto)] gap-x-3 gap-y-1 text-xs">
        <Row label="Planned capacity · Institution">
          {editable('institutionPlanned', 'Planned capacity · Institution', text(line.institutionPlanned), count(line.institutionPlanned), validateAmount)}
        </Row>
        <Row label="Planned capacity · Partner">
          {editable('partnerPlanned', 'Planned capacity · Partner', text(line.partnerPlanned), count(line.partnerPlanned), validateAmount)}
        </Row>
        <Row label="Target · Institution">
          {editable('institutionTarget', 'Target · Institution', text(line.institutionTarget), count(line.institutionTarget), validateAmount)}
        </Row>
        <Row label="Target · Partner">
          {editable('partnerTarget', 'Target · Partner', text(line.partnerTarget), count(line.partnerTarget), validateAmount)}
        </Row>
        <Row label="Travel %">
          {editable('travelPercent', 'Travel %', text(line.travelPercent), count(line.travelPercent), validatePercent)}
        </Row>
        <Row label="Months">
          {editable('months', 'Months', text(line.months), count(line.months), validateAmount)}
        </Row>
        <Row label="Unit price">
          {editable(
            'unitPriceOverrideUsd',
            'Unit price',
            text(line.unitPriceOverrideUsd),
            <Money amount={line.unitPriceUsd} currency="USD" />,
            validateAmount,
          )}
        </Row>
        <Row label="Travel unit price">
          <Money amount={line.travelUnitPriceUsd} currency="USD" />
        </Row>
        <Row label="Planned revenue">
          <Money amount={line.plannedRevenueUsd} currency="USD" />
        </Row>
        <Row label="Target revenue">
          <Money amount={line.targetRevenueUsd} currency="USD" />
        </Row>
        <Row label="Planned travel">
          <Money amount={line.plannedTravelRevenueUsd} currency="USD" />
        </Row>
        <Row label="Target travel">
          <Money amount={line.targetTravelRevenueUsd} currency="USD" />
        </Row>
        <Row label="Achieved">
          <Money amount={line.achievedUsd} currency="USD" />
        </Row>
        <Row label="Behind">
          <Money amount={line.gapUsd} currency="USD" />
        </Row>
        <Row label="% achieved">
          <span className="tabular-nums">
            {line.achievedPct === null || line.achievedPct === undefined ? '—' : `${count(line.achievedPct)}%`}
          </span>
        </Row>
        <Row label="Pace gap">
          <Money amount={line.paceGapUsd} currency="USD" />
        </Row>
        <Row label="Sold (inst / partner)">
          <span className="tabular-nums">
            {count(line.institutionsSold)} / {count(line.partnersSold)}
          </span>
        </Row>
        {poolName !== null && <Row label="Pool">{poolName}</Row>}
      </dl>
      <div className="text-xs">
        <p className="m-0 text-fg-muted">Notes</p>
        <InlineField
          label={`Notes · ${line.projectName}`}
          kind="multiline"
          rows={3}
          maxLength={500}
          value={text(line.notes)}
          disabled={!canEdit}
          onSave={(next) => save('notes', next)}
        />
      </div>
    </li>
  );
}

export function SalesTargetsCards({
  view,
  canEdit,
  onChanged,
}: {
  view: SalesTargetsView;
  canEdit: boolean;
  onChanged: () => void;
}) {
  const poolNames = new Map(view.pools.map((pool) => [pool.id, pool.name] as const));
  const groups = groupSalesTargetLines(view.lines);

  if (groups.length === 0) {
    return (
      <div className="md:hidden">
        <EmptyState title="No projects in this cycle have sales targets yet." />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 md:hidden">
      {groups.map((group) => (
        <section key={group.name} aria-label={group.name} className="min-w-0">
          <h3 className="m-0 text-sm font-semibold text-fg">{group.name}</h3>
          <ul className="m-0 flex list-none flex-col divide-y divide-border border-y border-border p-0">
            {group.lines.map((line) => (
              <LineCard
                key={line.projectId}
                line={line}
                view={view}
                poolName={line.poolId !== null ? (poolNames.get(line.poolId) ?? null) : null}
                canEdit={canEdit}
                onChanged={onChanged}
              />
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
